import { CloseCode, createClient } from "graphql-ws";

import { SubscriptionStatus } from "./subscriptionStatus";

import { authService } from "./OS/auth/authService";

import { config } from "./config";

import * as Sentry from "@sentry/browser";
import { refreshAuth } from "./OS/auth/refreshAuth";

import {
  Observable,
  RecordSource,
  Store,
  SubscribeFunction,
  TaskScheduler,
} from "relay-runtime";
import { unstable_batchedUpdates } from "react-dom";
import { createRelayEnvironmentWithMissingFieldHandlers , RelayNetwork } from "widgets";
import {
  GraphQLResponseWithExtensionsOnly,
  GraphQLSingularResponse,
} from "relay-runtime/lib/network/RelayNetworkTypes";

async function waitForSession(): Promise<{
  authToken: string;
  currentStoreId?: string;
}> {
  if (
    authService.state.matches("loggedIn") &&
    authService.state.context.authToken
  ) {
    return {
      authToken: authService.state.context.authToken,
      currentStoreId: authService.state.context.currentStoreId,
    };
  }

  return new Promise<{
    authToken: string;
    currentStoreId?: string;
  }>((resolve) => {
    const v = authService.subscribe((state) => {
      if (state.matches("loggedIn") && state.context.authToken) {
        v.unsubscribe();
        resolve({
          authToken: state.context.authToken,
          currentStoreId: state.context.authToken,
        });
      }
    });
  });
}

let activeSocket: WebSocket | undefined;
let timedOut: ReturnType<typeof setTimeout> | undefined;
export let currentLatency: number | undefined;
let lastLatency: number | undefined;
let lastPing = Date.now();

export function createRelayEnvironment(
  locale: string,
  setSubscriptionStatus: (status: SubscriptionStatus) => void
) {
  const subscriptionsClient = createClient({
    url: config.baseWSUrl + "/sub",
    keepAlive: 5000,
    retryAttempts: 1000,
    retryWait(retries) {
      return new Promise((resolve) =>
        setTimeout(resolve, 500 + retries * 500 * Math.random())
      );
    },
    shouldRetry: () => {
      return true;
    },
    connectionAckWaitTimeout: 10000,
    on: {
      connecting: () => {
        setSubscriptionStatus(SubscriptionStatus.connecting);
      },

      connected: (socket: any) => {
        setSubscriptionStatus(SubscriptionStatus.connected);

        activeSocket = socket;
      },
      closed: (event: any) => {
        console.log("closed");
        lastLatency = undefined;
        setSubscriptionStatus(SubscriptionStatus.closed);
        if (event?.code === CloseCode.Forbidden) {
        }
      },

      ping: (received) => {
        if (!received && timedOut === undefined) {
          lastPing = Date.now();

          timedOut = setTimeout(() => {
            if (activeSocket?.readyState === WebSocket.OPEN) {
              setSubscriptionStatus(SubscriptionStatus.closed);
              activeSocket?.close(4408, "Request Timeout");
            }
          }, 6000); // wait 3 seconds for the pong and then close the connection
        }
      },
      pong: (received) => {
        if (received && timedOut) {
          const latency = Math.max(Math.min(Date.now() - lastPing, 5000), 0);
          // moving avarage with k=2
          lastLatency = currentLatency;
          currentLatency =
            ((currentLatency ?? latency) + (lastLatency ?? latency) + latency) /
            3;

          clearTimeout(timedOut);
          timedOut = undefined;
        } // pong is received, clear connection close timeout
      },
    },
    connectionParams: async () => {
      console.log("waiting for connection params");
      const session = await waitForSession();
      console.log("params recived");

      return {
        authToken: session.authToken,
        storeId: session.currentStoreId,
        language: locale,
      };
    },
  });

  const subFN: SubscribeFunction = (operation, variables) => {
    return Observable.create((sink) => {
      if (!operation.text) {
        return sink.error(new Error("Operation text cannot be empty"));
      }

      return subscriptionsClient.subscribe(
        {
          operationName: operation.name,
          query: operation.text,
          variables,
        },
        sink as any
      );
    });
  };

  const network = RelayNetwork.create(
    async (req) => {
      console.log("🌐🌐 ", req.params.name);

      const session = await waitForSession();

      return {
        url: config.baseUrl + "/graphql",
        headers: {
          "x-storesprint-store-id": session?.currentStoreId ?? "",
          "accept-language": locale,
          "x-storesprint-locale": locale,
        },
      };
    },
    [
      (next) =>
        async function* (req) {
          Sentry.addBreadcrumb({
            category: "gql",
            message: "Query",
            data: { id: req.params.id ?? req.params.name },
          });
          try {
            for await (const _res of await next(req)) {
              const res = _res as Exclude<
                GraphQLSingularResponse,
                GraphQLResponseWithExtensionsOnly
              >;

              const errorCodes: string[] =
                res?.errors?.map((e) => (e as any)?.extensions?.code) ?? [];

              if (errorCodes.includes("STORECAST_SCREEN_DEVICE_UNDEFINED")) {
                authService.send("STORECAST_SCREEN_DEVICE_UNDEFINED");
              }
              if (errorCodes.includes("UNAUTHENTICATED")) {
                authService.send("STORECAST_SCREEN_DEVICE_UNDEFINED");
              }

              if (res.errors) {
                Sentry.addBreadcrumb({
                  category: "gql",
                  message: "Error",
                  data: { errorCodes },
                });
                Sentry.captureException(
                  new Error(RelayNetwork.formatPayloadErrors(req, res.errors))
                );
              } else {
                Sentry.addBreadcrumb({
                  category: "gql",
                  message: "Success",
                });
              }

              yield res;
            }
          } catch (e) {
            Sentry.addBreadcrumb({
              category: "gql",
              message: "Unknown error",
              data: { error: e },
            });
            throw e;
          }
        } satisfies RelayNetwork.MiddlewareFn,
      RelayNetwork.Middlewares.retry({
        timeout: 15000,
        retryDelays: [500, 3000, 5000, 11000],
        statusCodes: [429, 500, 503, 504],
      }),
      RelayNetwork.Middlewares.auth({
        token: async () => {
          const session = await waitForSession();
          return session.authToken;
        },
        refreshToken: async () => {
          return refreshAuth(authService).then((authToken) => {
            return authToken;
          });
        },
      }),
    ],
    subFN
  );

  const source = new RecordSource();
  const relayStore = new Store(source);

  const RelayScheduler: TaskScheduler = {
    cancel: () => {},
    schedule: (task) => {
      unstable_batchedUpdates(task);
      return "";
    },
  };
  const env = createRelayEnvironmentWithMissingFieldHandlers({
    network,
    store: relayStore,
    scheduler: RelayScheduler,
  });

  return {
    env,
    dispose: () => {
      subscriptionsClient.dispose();
    },
  };
}
